import { useState } from 'react';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, User, Phone, Mail, Shield, Facebook, Instagram, Edit, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

interface ProfileModalProps {
  onClose: () => void;
  onAdminLogin: () => void;
}

export default function ProfileModal({ onClose, onAdminLogin }: ProfileModalProps) {
  const { t } = useTranslation();
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditingPhoto, setIsEditingPhoto] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState('');
  const [tempPhotoUrl, setTempPhotoUrl] = useState('');

  useEffect(() => {
    // Check if user is already logged in as admin
    const adminStatus = localStorage.getItem('admin_logged_in');
    setIsAdmin(adminStatus === 'true');

    // Load profile photo from Supabase
    loadProfilePhoto();

    // Subscribe to real-time changes
    const channel = supabase
      .channel('site-settings-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'site_settings',
          filter: 'key=eq.profile_photo'
        },
        (payload) => {
          if (payload.new?.value) {
            setProfilePhoto(payload.new.value);
          }
        }
      )
      .subscribe();

    // Listen for custom events
    const handlePhotoUpdate = (e: CustomEvent) => {
      setProfilePhoto(e.detail.photoUrl);
    };

    window.addEventListener('profilePhotoUpdated', handlePhotoUpdate as EventListener);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('profilePhotoUpdated', handlePhotoUpdate as EventListener);
    };
  }, []);

  const loadProfilePhoto = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'profile_photo')
        .maybeSingle();

      if (error) throw error;

      if (data?.value) {
        setProfilePhoto(data.value);
      }
    } catch (error) {
      console.error('Error loading profile photo:', error);
    }
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/secure-files/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ username: loginData.username, password: loginData.password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) {
        throw new Error(data.error || 'Přihlášení selhalo');
      }

      setIsAdmin(true);
      localStorage.setItem('admin_logged_in', 'true');
      localStorage.setItem('admin_token', data.token);
      onAdminLogin();
      onClose();
    } catch (error) {
      console.error('Admin login error:', error);
    }
  };

  const handlePhotoEdit = () => {
    setTempPhotoUrl(profilePhoto);
    setIsEditingPhoto(true);
  };

  const handlePhotoSave = async () => {
    if (!tempPhotoUrl.trim()) {
      return;
    }

    try {
      // Save to Supabase
      const { error } = await supabase
        .from('site_settings')
        .update({ value: tempPhotoUrl })
        .eq('key', 'profile_photo');

      if (error) throw error;

      setProfilePhoto(tempPhotoUrl);
      setIsEditingPhoto(false);

      // Show success message
      const notification = document.createElement('div');
      notification.innerHTML = `
        <div style="
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        ">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Profilová fotka byla úspěšně změněna
        </div>
      `;

      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        pointer-events: none;
        transform: translateX(100%);
        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      `;

      document.body.appendChild(notification);

      // Slide in notification
      setTimeout(() => {
        notification.style.transform = 'translateX(0)';
      }, 100);

      // Slide out notification
      setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
      }, 2500);

      // Remove notification
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      // Trigger global update event so all instances update
      window.dispatchEvent(new CustomEvent('profilePhotoUpdated', { detail: { photoUrl: tempPhotoUrl } }));
    } catch (error) {
      console.error('Error saving profile photo:', error);
    }
  };

  const handlePhotoCancel = () => {
    setTempPhotoUrl('');
    setIsEditingPhoto(false);
  };

  const handleAdminLogout = () => {
    setIsAdmin(false);
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_token');
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-[#EE1C23]/30 rounded-2xl p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">{t('profile.title')}</h2>
          {isAdmin && (
            <div className="flex items-center space-x-2">
              <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold">
                {t('profile.admin')}
              </span>
            </div>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!showAdminLogin && !isEditingPhoto ? (
          <div className="space-y-6">
            {/* Profile Photo */}
            <div className="text-center">
              <div 
                className={`w-24 h-24 bg-gray-700 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden relative group ${
                  isAdmin ? 'cursor-pointer' : ''
                }`}
                onClick={isAdmin ? handlePhotoEdit : undefined}
              >
                {profilePhoto ? (
                  <img 
                    src={profilePhoto} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-gray-400" />
                )}
                {isAdmin && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Edit className="w-6 h-6 text-white" />
                  </div>
                )}
              </div>
              <h3 className="text-xl font-bold text-white">Ing. Letocha René</h3>
              <p className="text-gray-400">{t('profile.founder')}</p>
            </div>

            {/* Bio */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              <p className="text-gray-300 text-sm">
                {t('profile.bio')}
              </p>
            </div>

            {/* Social Media */}
            <div>
              <h4 className="text-white font-semibold mb-3">{t('profile.socialMedia')}</h4>
              <div className="grid grid-cols-2 gap-3">
                {/* Top row - Larger buttons */}
                <a href="https://www.instagram.com/reno_all4life/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 bg-gray-800 hover:bg-gray-700 px-4 py-4 rounded-lg transition-colors">
                  <Instagram className="w-6 h-6 text-pink-400" />
                  <span className="text-white font-medium">Instagram</span>
                </a>
                <a href="https://www.tiktok.com/@reno_cybertronic" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 bg-gray-800 hover:bg-gray-700 px-4 py-4 rounded-lg transition-colors">
                  <img src="/tiktok.svg" alt="TikTok" className="w-6 h-6" />
                  <span className="text-white font-medium">TikTok</span>
                </a>
                {/* Bottom row - Same size buttons */}
                <a href="https://www.facebook.com/cybertronic.unlimited" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 bg-gray-800 hover:bg-gray-700 px-4 py-4 rounded-lg transition-colors">
                  <Facebook className="w-5 h-5 text-blue-400" />
                  <span className="text-white font-medium">Facebook</span>
                </a>
                <a href="https://www.herohero.co/cybertronic" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 bg-gray-800 hover:bg-gray-700 px-4 py-4 rounded-lg transition-colors">
                  <img src="/img_20260205_104635.png" alt="HeroHero" className="w-5 h-5" />
                  <span className="text-white font-medium">HeroHero</span>
                </a>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-3">{t('profile.contact')}</h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-3 text-gray-300">
                  <Phone className="w-4 h-4 text-[#EE1C23]" />
                  <span>+420 777 875 098</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <Mail className="w-4 h-4 text-[#EE1C23]" />
                  <span>cybertronic@email.cz</span>
                </div>
              </div>
            </div>

            {/* Admin Buttons */}
            {!isAdmin ? (
              <button
                onClick={() => setShowAdminLogin(true)}
                className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-[#EE1C23] to-red-600 text-white py-3 rounded-lg hover:from-red-600 hover:to-[#EE1C23] transition-all"
              >
                <Shield className="w-4 h-4" />
                <span>{t('profile.admin')}</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setIsAdmin(false);
                  localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_token');
                  window.location.reload(); // Reload to update admin state across the app
                }}
                className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white py-3 rounded-lg hover:from-gray-500 hover:to-gray-600 transition-all"
              >
                <Shield className="w-4 h-4" />
                <span>{t('profile.logout')}</span>
              </button>
            )}
          </div>
        ) : isEditingPhoto ? (
          <div className="space-y-6">
            <div className="text-center">
              <Edit className="w-12 h-12 text-[#EE1C23] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white">{t('profile.changePhoto')}</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">{t('profile.imageUrl')}</label>
                <div className="space-y-3">
                  <input
                    type="url"
                    value={tempPhotoUrl}
                    onChange={(e) => setTempPhotoUrl(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none transition-colors"
                    placeholder="https://example.com/photo.jpg"
                  />
                  <div className="flex space-x-2">
                    <label className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2 px-4 rounded-lg cursor-pointer flex items-center justify-center transition-colors">
                      <Upload className="w-4 h-4 mr-2" />
                      {t('profile.uploadFile')}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setTempPhotoUrl(event.target?.result as string);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <div
                    className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center text-gray-400 hover:border-gray-500 transition-colors"
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      if (file && file.type.startsWith('image/')) {
                        if (file.size > 5 * 1024 * 1024) { // 5MB limit
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setTempPhotoUrl(event.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-500" />
                    <p className="text-sm">{t('profile.dragImage')}</p>
                  </div>
                </div>
              </div>

              {tempPhotoUrl && (
                <div className="text-center">
                  <div className="w-24 h-24 bg-gray-700 rounded-full mx-auto overflow-hidden">
                    <img 
                      src={tempPhotoUrl} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                  <p className="text-gray-400 text-sm mt-2">Náhled</p>
                  <p className="text-gray-400 text-sm mt-2">{t('profile.preview')}</p>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handlePhotoCancel}
                  className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handlePhotoSave}
                  className="flex-1 bg-gradient-to-r from-[#EE1C23] to-red-600 text-white py-3 rounded-lg hover:from-red-600 hover:to-[#EE1C23] transition-all"
                >
                  {t('profile.save')}
                </button>
              </div>
            </div>
          </div>
        ) : showAdminLogin ? (
          <div className="space-y-6">
            <div className="text-center">
              <Shield className="w-12 h-12 text-[#EE1C23] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white">{t('profile.adminLogin')}</h3>
            </div>

            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">{t('profile.username')}</label>
                <input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none transition-colors"
                  placeholder="admin"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">{t('admin.password')}</label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAdminLogin(false)}
                  className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {t('profile.back')}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-[#EE1C23] to-red-600 text-white py-3 rounded-lg hover:from-red-600 hover:to-[#EE1C23] transition-all"
                >
                  {t('profile.login')}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </motion.div>
    </motion.div>
  );
}